import { mapGlobalChecks, mapPersonChecks, isSamePerson } from "@/components/claim/details/idCardAnalysisFormatter";
import { mapChecks as mapInvoiceChecks } from "@/components/claim/details/purchaseInvoiceAnalysisFormatter";
import { mapChecks as mapComplaintsChecks } from "@/components/claim/details/complaintsReceiptAnalysisFormatter";
import { mapChecks as mapAntiTheftChecks } from "@/components/claim/details/antiTheftDeviceInvoiceAnalysisFormatter";
import { mapChecks as mapProductPhotosChecks } from "@/components/claim/details/productPhotosAnalysisFormatter";

// Étapes "document" du flow : type d'analyse + clé de la pièce jointe sur la déclaration,
// dans l'ordre d'affichage du flow. `analyzed: false` marque une pièce simplement affichée,
// dont l'analyse IA n'existe pas encore : à retirer le jour où elle est mise en place.
const DOCUMENT_STEPS = [
  { key: "id_card", analysisType: "id_card", documentKey: "idCard" },
  // Le justificatif titulaire est analysé avec la facture : il s'affiche dans la même étape.
  {
    key: "product_invoice",
    analysisType: "product_invoice",
    documentKey: "productInvoice",
    extraDocumentKeys: ["invoiceHolderProof"],
  },
  { key: "complaints_receipt", analysisType: "complaints_receipt", documentKey: "complaintsReceipt" },
  {
    key: "anti_theft_device_invoice",
    analysisType: "anti_theft_device_invoice",
    documentKey: "antiTheftDeviceInvoice",
  },
  { key: "product_photos", analysisType: "product_photos", documentKey: "productPhotos" },
];

// Fichiers joints d'un document, normalisés en tableau (l'API renvoie un tableau ou un objet seul).
function documentFiles(raw) {
  const files = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return files.filter((file) => file?.link);
}

// Un document est attendu dans l'analyse dès qu'il est joint à la déclaration.
function isAttached(declaration, documentKey) {
  return (declaration?.[documentKey]?.length || 0) > 0;
}

// Étapes du flow : le contexte du sinistre, l'alerte globale, puis une étape par document
// réellement joint. Le contexte vient en premier pour que le gestionnaire ait le sinistre en
// tête avant d'examiner les pièces. L'avis de l'agent (où l'on tranche) puis la prise en charge,
// ouverte par une approbation, ferment la marche quand le sinistre attend une décision —
// l'appelant tranche, ce module ne connaît pas le store. Hors décision, l'avis reste consultable
// dès qu'il a été rendu.
export function buildAnalysisSteps(declaration, { withDecision = false } = {}) {
  const documentSteps = DOCUMENT_STEPS.filter((step) => isAttached(declaration, step.documentKey)).map((step) => ({
    ...step,
    type: "document",
  }));
  const generalSteps =
    withDecision || declaration?.generalAnalysis?.verdict
      ? [{ key: "general_analysis", type: "general_analysis" }]
      : [];
  const handlingSteps = withDecision ? [{ key: "handling_type", type: "handling_type" }] : [];
  return [
    { key: "context", type: "context" },
    { key: "alerts", type: "alerts" },
    ...documentSteps,
    ...generalSteps,
    ...handlingSteps,
  ];
}

// Résumé d'un document pour AiAnalysisDocumentStep (analyse, verdict, checks, anomalies, sections).
export function documentSummary(t, declaration, step) {
  if (step?.type !== "document") return {};

  const { analysisType, documentKey, extraDocumentKeys = [] } = step;
  const analysis = analysisFor(declaration, analysisType);
  return {
    analysis,
    analyzedAt: analyzedAtFor(declaration, analysisType),
    // Repli sur overallVerdict, comme stepVerdict et le badge.
    verdict: verdictFor(declaration, analysisType) || analysis?.overallVerdict || null,
    documents: [documentKey, ...extraDocumentKeys].flatMap((key) => documentFiles(declaration?.[key])),
    checks: normalizedChecks(t, analysisType, analysis),
    summary: normalizedSummary(analysis),
    detailSections: analysisType === "id_card" ? idCardDetailSections(t, analysis) : [],
  };
}

// Un check est "à vérifier" dès que son verdict n'est pas conforme.
export function isToVerify(verdict) {
  return verdict && verdict !== "PASS";
}

export function badgeClassFor(verdict) {
  switch (verdict) {
    case "PASS":
      return "bg-green-100 text-green-800";
    case "REVIEW":
      return "bg-yellow-100 text-yellow-800";
    case "FAIL":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

// Verdict d'une étape du flow : null pour les étapes sans analyse (contexte, alertes, décision).
// Repli sur overallVerdict comme documentSummary et le badge, sans recalculer les checks.
export function stepVerdict(declaration, step) {
  if (step?.type !== "document") return null;

  const { analysisType } = step;
  return verdictFor(declaration, analysisType) || analysisFor(declaration, analysisType)?.overallVerdict || null;
}

// Tonalité de l'onglet portant ce verdict, sur la même échelle que badgeClassFor :
// l'onglet prend ainsi la couleur du badge affiché dans l'étape correspondante.
export function toneFor(verdict) {
  switch (verdict) {
    case "PASS":
      return "green";
    case "REVIEW":
      return "yellow";
    case "FAIL":
      return "red";
    default:
      return "gray";
  }
}

const GENERAL_VERDICT_TONES = { approve: "green", complement: "yellow", refuse: "red" };

// Tonalité de l'onglet de l'avis de l'agent général : neutre tant que l'avis n'est pas rendu.
export function generalAnalysisTone(declaration) {
  return GENERAL_VERDICT_TONES[declaration?.generalAnalysis?.verdict] || null;
}

// Nombre d'alertes affichées par ClaimAlerts : celles portées par le sinistre, plus
// l'alerte LCBFT, rendue dès que le client a un score.
export function alertsCount(claim, customer) {
  return (claim?.alerts?.length || 0) + (customer?.lcbftScore ? 1 : 0);
}

// Classe du bouton "Analyser le dossier", sur la tonalité de l'avis de l'agent général
// (generalAnalysisTone) : neutre tant que l'avis n'est pas rendu.
export function buttonClassFor(tone) {
  switch (tone) {
    case "green":
      return "bg-green-100 text-green-800 hover:bg-green-200";
    case "yellow":
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200";
    case "red":
      return "bg-red-100 text-red-800 hover:bg-red-200";
    default:
      return "bg-gray-100 text-gray-700 hover:bg-gray-200";
  }
}

function analysisFor(declaration, analysisType) {
  return declaration?.[`${camelize(analysisType)}Analysis`] || null;
}

function analyzedAtFor(declaration, analysisType) {
  return declaration?.[`${camelize(analysisType)}AnalyzedAt`] || null;
}

function verdictFor(declaration, analysisType) {
  return declaration?.[`${camelize(analysisType)}AnalysisVerdict`] || null;
}

function camelize(snake) {
  return snake.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Préfixe i18n des libellés de champ, par type de document (facture / plainte / photos).
const CHECK_LABEL_PREFIX = {
  product_invoice: "purchase_invoice_analysis.field",
  complaints_receipt: "complaints_receipt_analysis.field",
  product_photos: "product_photos_analysis.field",
};

// Formatter de checks par type de document (les types id_card / antivol ont leur propre branche).
const CHECK_MAPPERS = {
  product_invoice: mapInvoiceChecks,
  complaints_receipt: mapComplaintsChecks,
  product_photos: mapProductPhotosChecks,
};

// Normalise les checks d'une analyse vers { key, label, verdict, reason }.
export function normalizedChecks(t, analysisType, analysis) {
  if (!analysis) return [];

  if (analysisType === "id_card") {
    return [...mapGlobalChecks(t, analysis).map(toNormalizedCheck), ...idCardPersonChecks(t, analysis)];
  }

  // Le formatter antivol prend (t, analysis) et renvoie des labels déjà traduits (clés globales bo).
  if (analysisType === "anti_theft_device_invoice") {
    return mapAntiTheftChecks(t, analysis).map(toNormalizedCheck);
  }

  const mapChecks = CHECK_MAPPERS[analysisType] || mapComplaintsChecks;
  const prefix = CHECK_LABEL_PREFIX[analysisType];
  return mapChecks(analysis).map((check) => toNormalizedCheck(localizeCheckLabel(t, prefix, check)));
}

// Traduit le libellé du champ via l'i18n, en conservant le libellé du formatter si la clé est absente.
function localizeCheckLabel(t, prefix, check) {
  const key = `${prefix}.${check.field}`;
  const label = t(key);
  return label === key ? check : { ...check, label };
}

// Les vérifications par personne sont préfixées par leur rôle (souscripteur / déclarant)
// pour distinguer les deux comparaisons, comme le font les sections de la modale détaillée.
function idCardPersonChecks(t, analysis) {
  const roles = isSamePerson(analysis) ? ["policy_holder"] : ["policy_holder", "declarant"];
  return roles.flatMap((role) => {
    const roleLabel = t(`id_card_analysis.role.${role}`);
    return mapPersonChecks(t, analysis, role).map((check) => ({
      key: `${role}_${check.field}`,
      label: `${roleLabel} · ${check.label}`,
      verdict: check.verdict,
      reason: check.reason,
    }));
  });
}

function toNormalizedCheck(check) {
  return { key: check.field, label: check.label, verdict: check.verdict, reason: check.reason };
}

// Résumé rédigé par l'IA de ce qu'il y a à vérifier. Absent des analyses antérieures à sa mise
// en place, ou quand sa génération a échoué : l'étape retombe alors sur la liste des checks.
export function normalizedSummary(analysis) {
  const summary = analysis?.summary;
  return typeof summary === "string" ? summary.trim() : "";
}

// Tableaux détaillés de la carte d'identité, regroupés comme dans la modale :
// vérifications globales, puis souscripteur / déclarant (ou une seule section si même personne).
// Chaque section garde les checks bruts (avec extractedValue / expectedValue) pour VerificationTable.
export function idCardDetailSections(t, analysis) {
  if (!analysis) return [];

  const sections = [];
  const global = mapGlobalChecks(t, analysis);
  if (global.length) sections.push({ key: "global", title: t("id_card_section.global"), checks: global });

  const personKeys = isSamePerson(analysis) ? ["same_person"] : ["policy_holder", "declarant"];
  for (const key of personKeys) {
    const role = key === "same_person" ? "policy_holder" : key;
    const checks = mapPersonChecks(t, analysis, role);
    if (checks.length) {
      sections.push({
        key,
        title: t(`id_card_section.${key}`),
        description: t(`id_card_section_description.${key}`),
        checks,
      });
    }
  }

  return sections;
}
